/**
 * @module features/client-device-management/api/quotaApi
 * @description API fetchers and TanStack Query hooks for per-device-type client quotas
 * and cached fleet assignment analytics.
 */
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope, AppError } from "@/lib/api-client";
import {
  FleetAssignmentStats,
  FleetAssignmentStatsSchema,
  QuotaItem,
  QuotaItemSchema,
  SetQuotaFormValues,
} from "../types";
import { clientDeviceKeys, parsePayloadOrThrow } from "./assignmentApi";

const BASE = "/super-admin/client-devices";

/**
 * Fetches all configured per-device-type quotas and active assignment counts for a client.
 */
export async function getOrgDeviceQuotas(clientId: string): Promise<QuotaItem[]> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/clients/${clientId}/quotas/`
    );
    return parsePayloadOrThrow(
      z.array(QuotaItemSchema),
      res.data?.data ?? [],
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load organization device quotas.");
  }
}

/**
 * Creates or updates a maximum device allocation quota for a specific device_type on a client.
 */
export async function setOrgDeviceQuota(
  clientId: string,
  input: SetQuotaFormValues
): Promise<QuotaItem> {
  try {
    const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(
      `${BASE}/clients/${clientId}/quotas/`,
      input
    );
    return parsePayloadOrThrow(
      QuotaItemSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to update device quota limit.");
  }
}

/**
 * Removes a per-device-type quota cap from a client organization.
 */
export async function deleteOrgDeviceQuota(
  clientId: string,
  quotaId: string
): Promise<void> {
  try {
    await apiClient.delete(`${BASE}/clients/${clientId}/quotas/${quotaId}/`);
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to remove device quota limit.");
  }
}

/**
 * Retrieves global fleet assignment statistics cached in Redis (60s TTL).
 */
export async function getFleetAssignmentStats(): Promise<FleetAssignmentStats> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(`${BASE}/stats/`);
    return parsePayloadOrThrow(
      FleetAssignmentStatsSchema,
      res.data?.data,
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load fleet assignment metrics.");
  }
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

export function useOrgDeviceQuotas(clientId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: clientDeviceKeys.quotas(clientId ?? ""),
    queryFn: () => getOrgDeviceQuotas(clientId!),
    enabled: Boolean(clientId) && enabled,
  });
}

export function useSetOrgDeviceQuota(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetQuotaFormValues) => setOrgDeviceQuota(clientId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.quotas(clientId),
      });
    },
  });
}

export function useDeleteOrgDeviceQuota(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quotaId: string) => deleteOrgDeviceQuota(clientId, quotaId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.quotas(clientId),
      });
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.all,
      });
    },
  });
}

export function useFleetAssignmentStats() {
  return useQuery({
    queryKey: clientDeviceKeys.stats,
    queryFn: getFleetAssignmentStats,
    staleTime: 30_000,
  });
}

